import { useId } from "react";

import { Slider } from "./Slider";
import { BASEBALL_DIAMETER_INCHES } from "./strikeZonePresets";

// Shared SVG paths for the baseball icon — rendered twice (gray base + colored fill with clip)
const BaseballPaths = ({
  color,
  backgroundColor,
}: {
  color: string;
  backgroundColor: string;
}) => (
  <g fill="none">
    <path
      fill={color}
      d="M16 1C7.716 1 1 7.716 1 16s6.716 15 15 15s15-6.716 15-15S24.284 1 16 1"
    />
    <path
      fill={backgroundColor}
      d="M16 30c7.732 0 14-6.268 14-14S23.732 2 16 2S2 8.268 2 16s6.268 14 14 14"
    />
    <path
      fill={color}
      d="M29.973 16.88q-.03.504-.098.996c-8.242-.97-14.781-7.508-15.75-15.751q.492-.066.995-.098c.923 7.769 7.084 13.93 14.853 14.854M16.88 29.974c-.924-7.768-7.085-13.93-14.854-14.853q.032-.502.098-.996c8.243.97 14.782 7.509 15.751 15.751q-.492.066-.995.098"
    />
    <path
      fill={color}
      d="M15.094 3.11a.5.5 0 0 0-.533.153l-1.166 1.375a.5.5 0 0 0 .762.647l.95-1.12l1.4.446a.5.5 0 0 0 .305-.953zm.882 4.668a.5.5 0 0 1 .48-.277l1.8.116a.5.5 0 0 1-.065.998l-1.466-.095l-.652 1.318a.5.5 0 0 1-.896-.444zm2.595 3.813a.5.5 0 0 1 .392-.392l1.768-.354a.5.5 0 1 1 .196.981l-1.441.288l-.288 1.441a.5.5 0 1 1-.981-.196zm3.912 3.392a.5.5 0 0 1 .277-.48l1.616-.8a.5.5 0 1 1 .443.897l-1.317.651l.094 1.467a.5.5 0 0 1-.997.064zm4.616 1.592a.5.5 0 0 0-.119.542l.656 1.679a.5.5 0 0 0 .932-.364l-.535-1.369l1.057-1.02a.5.5 0 0 0-.694-.72zm-23.95-2.047a.5.5 0 0 0-.097.546l.725 1.65a.5.5 0 0 0 .915-.401l-.59-1.346l1.015-1.062a.5.5 0 1 0-.723-.691zm4.602 2.047a.5.5 0 0 1 .277-.48l1.616-.8a.5.5 0 0 1 .443.897l-1.317.651l.095 1.467a.5.5 0 0 1-.998.064zm3.82 2.766a.5.5 0 0 1 .391-.392l1.768-.353a.5.5 0 0 1 .196.98l-1.44.288l-.289 1.441a.5.5 0 0 1-.98-.196zm3.03 3.438a.5.5 0 0 1 .48-.278l1.799.116a.5.5 0 0 1-.064.998l-1.467-.094l-.651 1.317a.5.5 0 1 1-.896-.444zm2.469 4.13a.5.5 0 0 0-.538.137l-1.207 1.338a.5.5 0 1 0 .743.67l.984-1.091l1.386.488a.5.5 0 1 0 .332-.943z"
    />
  </g>
);

interface BaseballIconProps {
  fillRatio: number;
  backgroundColor?: string;
  fillColor?: string;
  outlineColor?: string;
  size: "sm" | "default";
}

const BASEBALL_SIZES = {
  sm: 15,
  default: 24,
} as const;

export const BaseballIcon = ({
  fillRatio,
  backgroundColor = "white",
  fillColor = "#2563eb",
  outlineColor = "#9ca3af",
  size = "default",
}: BaseballIconProps) => {
  const id = useId();
  const clipId = `baseball-clip-${id}`;
  const clipWidth = 32 * Math.max(0, Math.min(1, fillRatio));
  const px = BASEBALL_SIZES[size];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={px}
      height={px}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={clipWidth} height="32" />
        </clipPath>
      </defs>

      {/* Gray base layer */}
      <BaseballPaths color={outlineColor} backgroundColor={backgroundColor} />

      {/* Blue filled layer, clipped to fillRatio */}
      <g clipPath={`url(#${clipId})`}>
        <BaseballPaths color={fillColor} backgroundColor={backgroundColor} />
      </g>
    </svg>
  );
};

interface ExpansionSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
}

export const ExpansionSlider = ({
  label,
  value,
  onChange,
}: ExpansionSliderProps) => {
  const inchDisplay =
    value === 0 ? `0"` : `${(value * BASEBALL_DIAMETER_INCHES).toFixed(2)}"`;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <Slider
          value={value}
          onChange={onChange}
          min={0}
          max={3}
          step={0.5}
          width={250}
        />

        {/* Inch value */}
        <span className="text-md w-8 text-left shrink-0 font-bold">
          {inchDisplay}
        </span>

        {/* Baseball icons */}
        <div className="flex items-center gap-1 shrink-0">
          {[0, 1, 2].map((i) => (
            <BaseballIcon
              key={i}
              fillRatio={Math.max(0, Math.min(1, value - i))}
              size="default"
            />
          ))}
        </div>
      </div>
    </div>
  );
};
